import React, { useDeferredValue, useEffect, useRef, useState } from 'react';
import {
  ExternalLink,
  Link2,
  Pencil,
  Plus,
  Power,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { catalogApi } from '../../../services/catalogApi';
import type {
  CatalogEntity,
  CatalogEvent,
  CatalogEventSource,
  CatalogMetadata,
  EntityEventSourceMapping,
  EntityInput,
  EventSourceInput,
} from '../../../types/catalog';
import { displayEntityType } from '../../../utils/entityTerminology';
import {
  ButtonRow,
  Field,
  Input,
  Label,
  PrimaryButton,
  SecondaryButton,
} from '../AdminUI';
import { EntityForm, EventSourceForm } from './CatalogForms';
import { EntityWorkflow } from './EntityWorkflow';
import {
  ActionRow,
  Badge,
  CompactActionButton,
  CompactActions,
  DataTable,
  DetailHeader,
  DetailPane,
  DetailSubtitle,
  DetailTitle,
  EmptyState,
  ErrorBanner,
  Hint,
  InfoItem,
  InfoStrip,
  ListPane,
  Loading,
  PaneToolbar,
  ResultButton,
  ResultList,
  ResultMeta,
  ResultTitle,
  ResourceContent,
  ResourceDescription,
  ResourceList,
  ResourceMeta,
  ResourceRow,
  ResourceTitle,
  SearchInput,
  SearchRow,
  SearchSubmit,
  Section,
  SectionHeader,
  SectionTitle,
  SplitPane,
  TableWrap,
  formatDate,
  errorMessage,
} from './CatalogUI';

interface EntityManagerProps {
  metadata: CatalogMetadata;
  focusEntityId?: number | null;
  onOpenSource: (eventSourceId: number) => void;
  onOpenEvent: (eventId: number) => void;
}

export const EntityManager: React.FC<EntityManagerProps> = ({
  metadata,
  focusEntityId,
  onOpenSource,
  onOpenEvent,
}) => {
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const searchSequence = useRef(0);
  const [searchRefresh, setSearchRefresh] = useState(0);
  const [entities, setEntities] = useState<CatalogEntity[]>([]);
  const [selected, setSelected] = useState<CatalogEntity | null>(null);
  const [mappings, setMappings] = useState<EntityEventSourceMapping[]>([]);
  const [events, setEvents] = useState<CatalogEvent[]>([]);
  const [pastDays, setPastDays] = useState(30);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [addingSource, setAddingSource] = useState(false);
  const [editingSource, setEditingSource] = useState<CatalogEventSource | null>(null);
  const [linkingSource, setLinkingSource] = useState(false);
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourceResults, setSourceResults] = useState<CatalogEventSource[]>([]);

  const loadRelations = async (
    entityId: number,
    historyDays = pastDays,
    showLoading = true
  ) => {
    if (showLoading) setLoadingDetail(true);
    setError(null);
    try {
      const [nextMappings, nextEvents] = await Promise.all([
        catalogApi.listEntityEventSources({ entityId }),
        catalogApi.listEvents({ entityId, pastDays: historyDays, limit: 100 }),
      ]);
      setMappings(nextMappings);
      setEvents(nextEvents);
      return { mappings: nextMappings, events: nextEvents };
    } catch (loadError) {
      setError(errorMessage(loadError));
      return undefined;
    } finally {
      if (showLoading) setLoadingDetail(false);
    }
  };

  const chooseEntity = async (entity: CatalogEntity) => {
    setSelected(entity);
    setCreating(false);
    setEditing(false);
    setAddingSource(false);
    setEditingSource(null);
    setLinkingSource(false);
    await loadRelations(entity.entityId);
  };

  useEffect(() => {
    let active = true;
    const sequence = ++searchSequence.current;
    const timer = window.setTimeout(async () => {
      setLoadingList(true);
      setError(null);
      try {
        const result = await catalogApi.listEntities(deferredSearch.trim(), 50);
        if (active && sequence === searchSequence.current) setEntities(result);
      } catch (loadError) {
        if (active && sequence === searchSequence.current) setError(errorMessage(loadError));
      } finally {
        if (active && sequence === searchSequence.current) setLoadingList(false);
      }
    }, deferredSearch.trim() ? 250 : 0);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [deferredSearch, searchRefresh]);

  useEffect(() => {
    if (!focusEntityId || selected?.entityId === focusEntityId) return;
    setLoadingDetail(true);
    catalogApi.getEntity(focusEntityId)
      .then((entity) => {
        setEntities((current) => current.some((item) => item.entityId === entity.entityId)
          ? current
          : [entity, ...current]);
        return chooseEntity(entity);
      })
      .catch((loadError) => setError(errorMessage(loadError)))
      .finally(() => setLoadingDetail(false));
    // focusEntityId is the navigation signal; selected is intentionally not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusEntityId]);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setSearchRefresh((current) => current + 1);
  };

  const saveNewEntity = async (input: EntityInput) => {
    setBusy(true);
    setError(null);
    try {
      const entity = await catalogApi.createEntity(input);
      setEntities((current) => [entity, ...current.filter((item) => item.entityId !== entity.entityId)]);
      await chooseEntity(entity);
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setBusy(false);
    }
  };

  const saveEntity = async (input: EntityInput) => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const entity = await catalogApi.updateEntity(selected.entityId, input);
      setSelected(entity);
      setEntities((current) => current.map((item) => item.entityId === entity.entityId ? entity : item));
      setEditing(false);
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setBusy(false);
    }
  };

  const createAndLinkSource = async (input: EventSourceInput) => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const source = await catalogApi.createEventSource(input);
      await catalogApi.linkEntityEventSource(selected.entityId, source.eventSourceId);
      setAddingSource(false);
      await loadRelations(selected.entityId);
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setBusy(false);
    }
  };

  const saveSource = async (input: EventSourceInput) => {
    if (!editingSource || !selected) return;
    setBusy(true);
    setError(null);
    try {
      await catalogApi.updateEventSource(editingSource.eventSourceId, input);
      setEditingSource(null);
      await loadRelations(selected.entityId);
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setBusy(false);
    }
  };

  const searchSources = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setSourceResults(await catalogApi.listEventSources(sourceSearch, 25));
    } catch (searchError) {
      setError(errorMessage(searchError));
    } finally {
      setBusy(false);
    }
  };

  const linkExistingSource = async (source: CatalogEventSource) => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await catalogApi.linkEntityEventSource(selected.entityId, source.eventSourceId);
      await catalogApi.setEntityEventSourceActive(selected.entityId, source.eventSourceId, true);
      setLinkingSource(false);
      setSourceResults([]);
      setSourceSearch('');
      await loadRelations(selected.entityId);
    } catch (linkError) {
      setError(errorMessage(linkError));
    } finally {
      setBusy(false);
    }
  };

  const toggleMapping = async (mapping: EntityEventSourceMapping) => {
    setBusy(true);
    setError(null);
    try {
      await catalogApi.setEntityEventSourceActive(
        mapping.entityId,
        mapping.eventSourceId,
        !mapping.isActive
      );
      setMappings((current) => current.map((item) =>
        item.eventSourceId === mapping.eventSourceId
          ? { ...item, isActive: !mapping.isActive }
          : item
      ));
    } catch (toggleError) {
      setError(errorMessage(toggleError));
    } finally {
      setBusy(false);
    }
  };

  const unlinkSource = async (mapping: EntityEventSourceMapping) => {
    if (!selected || !window.confirm(`Remove this source from ${selected.entityName}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await catalogApi.unlinkEntityEventSource(mapping.entityId, mapping.eventSourceId);
      await loadRelations(selected.entityId);
    } catch (unlinkError) {
      setError(errorMessage(unlinkError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SplitPane>
      <ListPane>
        <PaneToolbar>
          <SearchRow onSubmit={submitSearch}>
            <SearchInput
              aria-label="Search entities"
              aria-autocomplete="list"
              aria-controls="catalog-entity-results"
              aria-expanded={entities.length > 0}
              role="combobox"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setEntities([]);
                setLoadingList(true);
              }}
              placeholder="Start typing an entity name"
            />
            <SearchSubmit label="Search entities" disabled={loadingList} />
          </SearchRow>
          <Hint aria-live="polite">
            {loadingList ? 'Finding entities...' : `${entities.length} matching entities`}
          </Hint>
          <ButtonRow>
            <PrimaryButton
              type="button"
              onClick={() => {
                setCreating(true);
                setSelected(null);
                setEditing(false);
              }}
            >
              <Plus size={16} /> New entity
            </PrimaryButton>
          </ButtonRow>
        </PaneToolbar>
        {loadingList && entities.length === 0 ? <Loading label="Loading entities" /> : (
          <ResultList id="catalog-entity-results" role="listbox">
            {entities.length === 0 && <EmptyState>No entities found.</EmptyState>}
            {entities.map((entity) => (
              <ResultButton
                type="button"
                role="option"
                aria-selected={selected?.entityId === entity.entityId}
                key={entity.entityId}
                $selected={selected?.entityId === entity.entityId}
                onClick={() => chooseEntity(entity)}
              >
                <ResultTitle>{entity.entityName}</ResultTitle>
                <ResultMeta>
                  <Badge>{displayEntityType(entity.entityType)}</Badge>
                  <span>#{entity.entityId}</span>
                </ResultMeta>
              </ResultButton>
            ))}
          </ResultList>
        )}
      </ListPane>

      <DetailPane>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {creating ? (
          <>
            <DetailHeader>
              <div>
                <DetailTitle>New entity</DetailTitle>
                <DetailSubtitle>Create a complete catalog record.</DetailSubtitle>
              </div>
            </DetailHeader>
            <EntityForm
              entityTypes={metadata.entityTypes}
              busy={busy}
              onSubmit={saveNewEntity}
              onCancel={() => setCreating(false)}
            />
          </>
        ) : !selected ? (
          <EmptyState>Select an entity to manage its fields, sources, and events.</EmptyState>
        ) : loadingDetail ? (
          <Loading label="Loading entity relationships" />
        ) : (
          <>
            <DetailHeader>
              <div>
                <DetailTitle>{selected.entityName}</DetailTitle>
                <DetailSubtitle>{displayEntityType(selected.entityType)} · Entity #{selected.entityId}</DetailSubtitle>
              </div>
              <ActionRow>
                <SecondaryButton type="button" onClick={() => setEditing(!editing)}>
                  <Pencil size={16} /> Edit entity
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  onClick={() => loadRelations(selected.entityId)}
                  disabled={busy}
                >
                  <RefreshCw size={16} /> Refresh
                </SecondaryButton>
              </ActionRow>
            </DetailHeader>

            <InfoStrip aria-label="Entity information">
              <InfoItem>
                <dt>One ID</dt>
                <dd>{selected.oneId ?? 'Not set'}</dd>
              </InfoItem>
              <InfoItem>
                <dt>Search name</dt>
                <dd>{selected.searchFriendlyName || 'Generated on next WebIQ run'}</dd>
              </InfoItem>
              <InfoItem>
                <dt>Contact</dt>
                <dd>{selected.entityContact || 'Not set'}</dd>
              </InfoItem>
              <InfoItem>
                <dt>Entity info</dt>
                <dd>{selected.entityInfo || 'Not set'}</dd>
              </InfoItem>
              <InfoItem>
                <dt>Official domain</dt>
                <dd>{selected.officialDomain || 'Not set'}</dd>
              </InfoItem>
              <InfoItem>
                <dt>Primary location</dt>
                <dd>
                  {selected.primaryLatitude != null && selected.primaryLongitude != null
                    ? `${selected.primaryLocationCity || 'Location'} (${selected.primaryLatitude}, ${selected.primaryLongitude})`
                    : 'Not set'}
                </dd>
              </InfoItem>
              <InfoItem>
                <dt>Event fallback</dt>
                <dd>{selected.eventLocationFallbackEnabled ? 'Verified and enabled' : 'Disabled'}</dd>
              </InfoItem>
            </InfoStrip>

            <EntityWorkflow
              key={selected.entityId}
              entity={selected}
              mappings={mappings}
              eventCount={events.length}
              onRefresh={() => loadRelations(selected.entityId, pastDays, false)}
            />

            {editing && (
              <Section>
                <SectionHeader><SectionTitle>Entity fields</SectionTitle></SectionHeader>
                <EntityForm
                  entityTypes={metadata.entityTypes}
                  entity={selected}
                  busy={busy}
                  onSubmit={saveEntity}
                  onCancel={() => setEditing(false)}
                />
              </Section>
            )}

            <Section>
              <SectionHeader>
                <SectionTitle>Event sources ({mappings.length})</SectionTitle>
                <ActionRow>
                  <SecondaryButton type="button" onClick={() => setLinkingSource(!linkingSource)}>
                    <Link2 size={16} /> Link existing
                  </SecondaryButton>
                  <PrimaryButton type="button" onClick={() => setAddingSource(!addingSource)}>
                    <Plus size={16} /> New source
                  </PrimaryButton>
                </ActionRow>
              </SectionHeader>

              {addingSource && (
                <Section>
                  <SectionTitle>Create and link source</SectionTitle>
                  <EventSourceForm
                    eventSourceTypes={metadata.eventSourceTypes}
                    busy={busy}
                    onSubmit={createAndLinkSource}
                    onCancel={() => setAddingSource(false)}
                  />
                </Section>
              )}

              {linkingSource && (
                <Section>
                  <SectionTitle>Find an existing source</SectionTitle>
                  <SearchRow onSubmit={searchSources}>
                    <SearchInput
                      aria-label="Search event sources to link"
                      value={sourceSearch}
                      onChange={(event) => setSourceSearch(event.target.value)}
                      placeholder="URL, name, or source info"
                    />
                    <SearchSubmit label="Search event sources" disabled={busy} />
                  </SearchRow>
                  {sourceResults.length > 0 && (
                    <TableWrap>
                      <DataTable>
                        <thead><tr><th>Source</th><th>Type</th><th>Action</th></tr></thead>
                        <tbody>
                          {sourceResults.map((source) => (
                            <tr key={source.eventSourceId}>
                              <td>{source.source}</td>
                              <td>{source.eventSourceType}</td>
                              <td>
                                <SecondaryButton type="button" onClick={() => linkExistingSource(source)}>
                                  <Link2 size={15} /> Link
                                </SecondaryButton>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </DataTable>
                    </TableWrap>
                  )}
                </Section>
              )}

              {mappings.length === 0 ? <EmptyState>No event sources linked.</EmptyState> : (
                <ResourceList>
                  {mappings.map((mapping) => (
                    <ResourceRow key={mapping.eventSourceId}>
                      <ResourceContent>
                        <ResourceTitle>{mapping.source}</ResourceTitle>
                        {mapping.sourceInfo && (
                          <ResourceDescription>{mapping.sourceInfo}</ResourceDescription>
                        )}
                        <ResourceMeta>
                            <Badge $tone={mapping.isActive ? 'success' : 'warning'}>
                              {mapping.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                            {mapping.eventSourceType !== 'Website' && <Badge>{mapping.eventSourceType}</Badge>}
                            <span>{mapping.sourceOrigin === 'manual' ? 'Manual' : 'WebIQ'}</span>
                            <span>Source #{mapping.eventSourceId}</span>
                        </ResourceMeta>
                      </ResourceContent>
                      <CompactActions>
                        <CompactActionButton
                          type="button"
                          onClick={() => toggleMapping(mapping)}
                          disabled={busy}
                        >
                          <Power size={14} /> {mapping.isActive ? 'Deactivate' : 'Activate'}
                        </CompactActionButton>
                        <CompactActionButton
                          type="button"
                          onClick={() => setEditingSource({
                            eventSourceId: mapping.eventSourceId,
                            eventSourceTypeId: metadata.eventSourceTypes.find(
                              (type) => type.name === mapping.eventSourceType
                            )?.id ?? 0,
                            eventSourceType: mapping.eventSourceType,
                            source: mapping.source,
                            sourceInfo: mapping.sourceInfo,
                          })}
                        >
                          <Pencil size={14} /> Edit
                        </CompactActionButton>
                        <CompactActionButton
                          type="button"
                          onClick={() => onOpenSource(mapping.eventSourceId)}
                        >
                          <ExternalLink size={14} /> Open
                        </CompactActionButton>
                        <CompactActionButton
                          type="button"
                          $tone="danger"
                          onClick={() => unlinkSource(mapping)}
                          disabled={busy}
                        >
                          <Trash2 size={14} /> Unlink
                        </CompactActionButton>
                      </CompactActions>
                    </ResourceRow>
                  ))}
                </ResourceList>
              )}

              {editingSource && (
                <Section>
                  <SectionTitle>Edit source #{editingSource.eventSourceId}</SectionTitle>
                  <EventSourceForm
                    eventSourceTypes={metadata.eventSourceTypes}
                    eventSource={editingSource}
                    busy={busy}
                    onSubmit={saveSource}
                    onCancel={() => setEditingSource(null)}
                  />
                </Section>
              )}
            </Section>

            <Section>
              <SectionHeader>
                <SectionTitle>Linked Event Info ({events.length})</SectionTitle>
                <ActionRow>
                  <Field style={{ margin: 0, width: 130 }}>
                    <Label htmlFor="entity-past-days">Past days</Label>
                    <Input
                      id="entity-past-days"
                      type="number"
                      min={0}
                      max={3650}
                      value={pastDays}
                      onChange={(event) => setPastDays(Number(event.target.value))}
                    />
                  </Field>
                  <SecondaryButton
                    type="button"
                    onClick={() => loadRelations(selected.entityId, pastDays)}
                  >
                    <RefreshCw size={16} /> Apply
                  </SecondaryButton>
                </ActionRow>
              </SectionHeader>
              {events.length === 0 ? <EmptyState>No events in this date window.</EmptyState> : (
                <ResourceList>
                  {events.map((catalogEvent) => (
                    <ResourceRow key={catalogEvent.eventId}>
                      <ResourceContent>
                        <ResourceTitle>{catalogEvent.title}</ResourceTitle>
                        {catalogEvent.description && (
                          <ResourceDescription>{catalogEvent.description}</ResourceDescription>
                        )}
                        <ResourceMeta>
                          <span>#{catalogEvent.eventId}</span>
                          <span>{formatDate(catalogEvent.startDate)} - {formatDate(catalogEvent.endDate)}</span>
                          {catalogEvent.type && <Badge>{catalogEvent.type}</Badge>}
                          <span>
                            {catalogEvent.isVirtual
                              ? 'Virtual'
                              : [catalogEvent.city, catalogEvent.state, catalogEvent.country]
                                .filter(Boolean).join(', ') || 'Location not set'}
                          </span>
                          <span>{catalogEvent.entityCount} entities</span>
                          <span>{catalogEvent.eventSourceCount} sources</span>
                        </ResourceMeta>
                      </ResourceContent>
                      <CompactActions>
                        <CompactActionButton
                          type="button"
                          onClick={() => onOpenEvent(catalogEvent.eventId)}
                        >
                          <ExternalLink size={14} /> Open event
                        </CompactActionButton>
                      </CompactActions>
                    </ResourceRow>
                  ))}
                </ResourceList>
              )}
            </Section>
          </>
        )}
      </DetailPane>
    </SplitPane>
  );
};